import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TagPill } from '@aibuild/ui';
import useAuth from '@contexts/useAuth';
import type { OrgRole } from '@contexts/AuthContextStore';
import useSnackbar from '@hooks/useSnackbar';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { ORG_ROLES, ORG_ROLE_LABELS } from '@constants/index';
import { accountExpiryBounds } from '@utils/accountExpiry';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import SettingSwitch from '@components/2-molecules/SettingSwitch';
import SettingInfo from '@components/2-molecules/SettingInfo';
import HorizontalDivider from '@components/1-atoms/HorizontalDivider';
import IconButton, { ICON_BUTTON_VARIANT_STANDARD } from '@components/1-atoms/IconButton';
import SpriteSVG from '@components/1-atoms/SpriteSVG';
import DropDownField, {
  DROP_DOWN_FIELD_SIZE_COMPACT,
} from '@components/1-atoms/DropDownField';
import Button, {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_OUTLINE,
  BUTTON_VARIANT_TEXT,
} from '@components/1-atoms/Button';
import {
  createOrganization,
  getCurrentOrganization,
  updateOrganization,
  leaveOrganization,
  changeRole,
  removeMember,
  sendInvitation,
  listInvitations,
  revokeInvitation,
  updateMfaEnforcement,
  setMemberExpiry,
  MemberLimitError,
} from '@api/organizations';
import type {
  OrgResponse,
  MemberResponse,
  InvitationResponse,
} from '@api/organizations';
import { Frame, TableContainer } from '../Settings.styled';
import {
  Wrapper,
  CurrentUserInfo,
  OrgNameRow,
  OrgNameLabel,
  OrgNameValue,
  EditRow,
  TypeRow,
  TypeLabel,
  TypeBadge,
  Input,
  DateInput,
  ButtonRow,
  FrameButtonRow,
  InviteForm,
  InputGroup,
  CompactInputGroup,
  SmallLabel,
  InviteRow,
  InviteInfo,
  InviteEmail,
  InviteMeta,
  MemberTable,
  ActionsCell,
  SmallIconButton,
  RoleBadge,
  RoleDropdownCell,
  CompactRemoveButton,
  InviteButtonWrapper,
  DangerButtonWrapper,
  HintText,
  CreateOrgForm,
  SectionDescription,
  NameCell,
  InlineChips,
} from './Organization.styled';
import OrgGroupsSection from './OrgGroupsSection';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}

const CONFIRM_CLOSED: ConfirmState = {
  open: false,
  title: '',
  message: '',
  confirmLabel: 'OK',
  onConfirm: () => {},
};

const Organization: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();

  const [org, setOrg] = useState<OrgResponse | null>(null);
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<ConfirmState>(CONFIRM_CLOSED);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>(ORG_ROLES.MEMBER);
  const [inviteSending, setInviteSending] = useState(false);

  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);

  const currentMember = members.find(
    (m) => m.email?.toLowerCase() === user?.email?.toLowerCase(),
  );
  const effectiveRole = currentMember?.orgRole ?? user?.orgRole;
  const isAibuildAdmin = effectiveRole === ORG_ROLES.AIBUILD_ADMIN;
  const isAdmin = isAibuildAdmin || effectiveRole === ORG_ROLES.ADMIN;

  const [editingExpiryUserId, setEditingExpiryUserId] = useState<string | null>(null);
  const [editExpiryValue, setEditExpiryValue] = useState('');

  const openConfirm = useCallback(
    (opts: Omit<ConfirmState, 'open' | 'onConfirm'>, action: () => void) => {
      setConfirm({ ...opts, open: true, onConfirm: action });
    },
    [],
  );

  const fetchData = useCallback(async () => {
    if (!user?.token) return;
    try {
      setPageError(null);
      const data = await getCurrentOrganization(user.token);
      setOrg(data.organization);
      setMembers(data.members);
      setEditName(data.organization.name);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchInvitations = useCallback(async () => {
    if (!user?.token || !isAdmin) {
      setInvitations([]);
      return;
    }
    try {
      const invites = await listInvitations(user.token);
      setInvitations(invites);
    } catch {
      setInvitations([]);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const refreshAll = useCallback(() => {
    fetchData();
    fetchInvitations();
  }, [fetchData, fetchInvitations]);

  const handleUpdateName = async () => {
    if (!user?.token || !editName.trim()) return;
    try {
      await updateOrganization(user.token, editName.trim());
      setEditing(false);
      showSuccess(intl.formatMessage({ id: 'org.name.updated', defaultMessage: 'Organization name updated' }));
      refreshAll();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update name');
    }
  };

  const handleLeave = () => {
    if (!user?.token) return;
    const { token } = user;
    openConfirm(
      {
        title: intl.formatMessage({ id: 'org.leave.title', defaultMessage: 'Leave Organization' }),
        message: intl.formatMessage({
          id: 'org.leave.message',
          defaultMessage: 'You will lose access to all organization assets and revert to your personal plan. This action cannot be undone.',
        }),
        confirmLabel: intl.formatMessage({ id: 'org.leave.confirm', defaultMessage: 'Leave Organization' }),
      },
      async () => {
        setConfirm(CONFIRM_CLOSED);
        try {
          await leaveOrganization(token);
          showSuccess(intl.formatMessage({ id: 'org.leave.success', defaultMessage: 'Left organization. Returning to personal organization.' }));
          setTimeout(() => globalThis.location.reload(), 800);
        } catch (err) {
          showError(err instanceof Error ? err.message : 'Failed to leave organization');
        }
      },
    );
  };

  const handleChangeRole = useCallback(async (userId: string, newRole: OrgRole) => {
    if (!user?.token) return;
    try {
      await changeRole(user.token, userId, newRole);
      showSuccess(intl.formatMessage({ id: 'org.role.updated', defaultMessage: 'Role updated' }));
      refreshAll();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to change role');
    }
  }, [user, intl, showSuccess, showError, refreshAll]);

  const handleRemoveMember = (userId: string, email: string) => {
    if (!user?.token) return;
    const { token } = user;
    openConfirm(
      {
        title: intl.formatMessage({ id: 'org.remove.title', defaultMessage: 'Remove Member' }),
        message: intl.formatMessage(
          { id: 'org.remove.message', defaultMessage: 'Remove {email} from the organization?' },
          { email },
        ),
        confirmLabel: intl.formatMessage({ id: 'org.remove.confirm', defaultMessage: 'Remove' }),
      },
      async () => {
        setConfirm(CONFIRM_CLOSED);
        try {
          await removeMember(token, userId);
          showSuccess(intl.formatMessage(
            { id: 'org.remove.success', defaultMessage: '{email} removed' },
            { email },
          ));
          refreshAll();
        } catch (err) {
          showError(err instanceof Error ? err.message : 'Failed to remove member');
        }
      },
    );
  };

  const handleInvite = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.token || !inviteEmail.trim()) return;
    setInviteSending(true);
    try {
      await sendInvitation(user.token, inviteEmail.trim(), inviteRole);
      showSuccess(intl.formatMessage(
        { id: 'org.invite.success', defaultMessage: 'Invitation sent to {email}' },
        { email: inviteEmail },
      ));
      setInviteEmail('');
      setInviteRole(ORG_ROLES.MEMBER);
      refreshAll();
    } catch (err) {
      if (err instanceof MemberLimitError) {
        openConfirm(
          {
            title: intl.formatMessage({ id: 'org.memberLimit.title', defaultMessage: 'Team size limit reached' }),
            message: intl.formatMessage(
              {
                id: 'org.memberLimit.message',
                defaultMessage:
                  'Your team has reached the {limit}-member limit. To add more seats, contact us at support@ai-build.com.',
              },
              { limit: err.limit },
            ),
            confirmLabel: intl.formatMessage({ id: 'org.memberLimit.cta', defaultMessage: 'OK' }),
          },
          () => setConfirm(CONFIRM_CLOSED),
        );
      } else {
        showError(err instanceof Error ? err.message : 'Failed to send invitation');
      }
    } finally {
      setInviteSending(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    if (!user?.token) return;
    try {
      await revokeInvitation(user.token, inviteId);
      showSuccess(intl.formatMessage(
        { id: 'org.invite.revoked', defaultMessage: 'Invitation to {email} revoked' },
        { email },
      ));
      refreshAll();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    }
  };

  const expiryBounds = accountExpiryBounds();

  const handleSetExpiry = async (userId: string) => {
    if (!user?.token || !editExpiryValue) {
      showError(intl.formatMessage({ id: 'org.expiry.selectDate', defaultMessage: 'Please select an expiry date' }));
      return;
    }
    try {
      const expiryDate = new Date(editExpiryValue).toISOString();
      await setMemberExpiry(user.token, userId, expiryDate);
      showSuccess(intl.formatMessage({ id: 'org.expiry.updated', defaultMessage: 'Expiry date updated' }));
      setEditingExpiryUserId(null);
      setEditExpiryValue('');
      refreshAll();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update expiry');
    }
  };

  const handleCreateOrg = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.token || !newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      await createOrganization(user.token, newOrgName.trim());
      showSuccess(intl.formatMessage({ id: 'org.create.success', defaultMessage: 'Organization created! Reloading...' }));
      setTimeout(() => globalThis.location.reload(), 800);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleMfaToggle = useCallback(() => {
    if (!user?.token || !org) return;
    const { token } = user;
    const newValue = !org.mfaEnforced;
    const action = newValue ? 'enable' : 'disable';
    openConfirm(
      {
        title: intl.formatMessage({ id: 'org.mfa.title', defaultMessage: 'MFA Enforcement' }),
        message: intl.formatMessage(
          { id: 'org.mfa.message', defaultMessage: 'Are you sure you want to {action} MFA enforcement for all organization members?' },
          { action },
        ),
        confirmLabel: newValue
          ? intl.formatMessage({ id: 'org.mfa.enable', defaultMessage: 'Enable' })
          : intl.formatMessage({ id: 'org.mfa.disable', defaultMessage: 'Disable' }),
      },
      async () => {
        setConfirm(CONFIRM_CLOSED);
        try {
          const updated = await updateMfaEnforcement(token, newValue);
          setOrg(updated);
          showSuccess(intl.formatMessage(
            { id: 'org.mfa.success', defaultMessage: 'MFA enforcement {action}d' },
            { action },
          ));
        } catch (err) {
          showError(err instanceof Error ? err.message : 'Failed to update MFA enforcement');
        }
      },
    );
  }, [user, org, openConfirm, intl, showSuccess, showError]);

  const buildRoleMenuItems = useCallback(
    (memberId: string, currentRole: OrgRole) => {
      const items: Array<{ label: string; selected: boolean; onClick: () => void }> = [];
      if (isAibuildAdmin) {
        items.push({
          label: ORG_ROLE_LABELS[ORG_ROLES.AIBUILD_ADMIN],
          selected: currentRole === ORG_ROLES.AIBUILD_ADMIN,
          onClick: () => handleChangeRole(memberId, ORG_ROLES.AIBUILD_ADMIN),
        });
      }
      items.push(
        {
          label: ORG_ROLE_LABELS[ORG_ROLES.ADMIN],
          selected: currentRole === ORG_ROLES.ADMIN,
          onClick: () => handleChangeRole(memberId, ORG_ROLES.ADMIN),
        },
        {
          label: ORG_ROLE_LABELS[ORG_ROLES.MEMBER],
          selected: currentRole === ORG_ROLES.MEMBER,
          onClick: () => handleChangeRole(memberId, ORG_ROLES.MEMBER),
        },
      );
      return items;
    },
    [isAibuildAdmin, handleChangeRole],
  );

  const inviteRoleMenuItems = useMemo(
    () => [
      {
        label: ORG_ROLE_LABELS[ORG_ROLES.MEMBER],
        selected: inviteRole === ORG_ROLES.MEMBER,
        onClick: () => setInviteRole(ORG_ROLES.MEMBER),
      },
      {
        label: ORG_ROLE_LABELS[ORG_ROLES.ADMIN],
        selected: inviteRole === ORG_ROLES.ADMIN,
        onClick: () => setInviteRole(ORG_ROLES.ADMIN),
      },
    ],
    [inviteRole],
  );

  if (loading) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({ id: 'org.title', defaultMessage: 'Organization' })}
        />
        <Frame>
          <SettingInfo
            label={intl.formatMessage({ id: 'org.loading', defaultMessage: 'Loading...' })}
          />
        </Frame>
      </Wrapper>
    );
  }

  if (!org) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({ id: 'org.title', defaultMessage: 'Organization' })}
        />
        {pageError && (
          <Frame>
            <SettingInfo label={pageError} />
          </Frame>
        )}
      </Wrapper>
    );
  }

  const isPersonalOrg = org.isOwnPersonalOrg;
  const orgTypeVariant = isPersonalOrg ? 'personal' : 'team';
  const orgTypeIcon = isPersonalOrg ? 'person_0' : 'corporate_fare';
  const orgTypeLabel = isPersonalOrg
    ? intl.formatMessage({ id: 'org.type.personal', defaultMessage: 'Personal' })
    : intl.formatMessage({ id: 'org.type.team', defaultMessage: 'Team' });

  return (
    <Wrapper data-testid="organization-settings">
      <ConfirmDialog
        show={confirm.open}
        title={confirm.title}
        confirmLabel={confirm.confirmLabel}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(CONFIRM_CLOSED)}
      >
        <p>{confirm.message}</p>
      </ConfirmDialog>

      <CurrentUserInfo>
        {intl.formatMessage({ id: 'org.loggedInAs', defaultMessage: 'Logged in as' })}{' '}
        <strong>{user?.email}</strong>
        {effectiveRole && <> &middot; {ORG_ROLE_LABELS[effectiveRole] || effectiveRole}</>}
      </CurrentUserInfo>

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'org.info.title', defaultMessage: 'Organization' })}
      />
      <Frame>
        {editing && isAdmin ? (
          <EditRow>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={intl.formatMessage({ id: 'org.name.placeholder', defaultMessage: 'Organization name' })}
            />
            <Button variant={BUTTON_VARIANT_PRIMARY} onClick={handleUpdateName} stretch={false}>
              {intl.formatMessage({ id: 'org.save', defaultMessage: 'Save' })}
            </Button>
            <Button
              variant={BUTTON_VARIANT_TEXT}
              onClick={() => { setEditing(false); setEditName(org.name); }}
              stretch={false}
            >
              {intl.formatMessage({ id: 'org.cancel', defaultMessage: 'Cancel' })}
            </Button>
          </EditRow>
        ) : (
          <OrgNameRow>
            <OrgNameLabel>
              {intl.formatMessage({ id: 'org.name.label', defaultMessage: 'Name' })}
            </OrgNameLabel>
            <OrgNameValue>
              {org.name}
              {isAdmin && (
                <SmallIconButton>
                  <IconButton
                    dataTestId="org-edit-name-button"
                    iconName="edit_0"
                    variant={ICON_BUTTON_VARIANT_STANDARD}
                    onClick={() => setEditing(true)}
                    tooltip={{ message: intl.formatMessage({ id: 'org.edit', defaultMessage: 'Edit' }) }}
                  />
                </SmallIconButton>
              )}
            </OrgNameValue>
          </OrgNameRow>
        )}

        <HorizontalDivider middleInset />

        <TypeRow>
          <TypeLabel>
            {intl.formatMessage({ id: 'org.type', defaultMessage: 'Type' })}
          </TypeLabel>
          <TypeBadge $variant={orgTypeVariant}>
            <SpriteSVG name={orgTypeIcon} />
            {orgTypeLabel}
          </TypeBadge>
        </TypeRow>
      </Frame>

      {!org.isPersonal && (
        <>
          <PageHeader
            variant={PAGE_HEADER_VARIANT_SMALL}
            title={intl.formatMessage({ id: 'org.security.title', defaultMessage: 'Security' })}
          />
          <Frame>
            <SettingSwitch
              label={intl.formatMessage({ id: 'org.mfa.label', defaultMessage: 'Require two-factor authentication' })}
              supportingText={
                org.mfaEnforced
                  ? intl.formatMessage({ id: 'org.mfa.enabledDescription', defaultMessage: 'All members must enable 2FA before accessing the platform.' })
                  : intl.formatMessage({ id: 'org.mfa.disabledDescription', defaultMessage: '2FA is optional for members.' })
              }
              switchProps={{
                disabled: !isAdmin,
                enabled: org.mfaEnforced,
                onChange: handleMfaToggle,
              }}
            />
            {!isAdmin && (
              <HintText>
                {intl.formatMessage({ id: 'org.mfa.adminOnly', defaultMessage: 'Only organization admins can change this setting.' })}
              </HintText>
            )}
          </Frame>
        </>
      )}

      {!org.isPersonal && user?.token && (
        <OrgGroupsSection
          token={user.token}
          isAdmin={isAdmin}
          members={members}
          onChanged={fetchData}
        />
      )}

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage(
          { id: 'org.members.title', defaultMessage: 'Members ({count})' },
          { count: members.length },
        )}
      />
      <Frame>
        <TableContainer>
          <MemberTable>
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'org.members.name', defaultMessage: 'Name' })}</th>
                <th>{intl.formatMessage({ id: 'org.members.email', defaultMessage: 'Email' })}</th>
                <th>{intl.formatMessage({ id: 'org.members.role', defaultMessage: 'Role' })}</th>
                {isAibuildAdmin && <th>{intl.formatMessage({ id: 'org.members.expiry', defaultMessage: 'Expiry' })}</th>}
                {isAdmin && <th>{intl.formatMessage({ id: 'org.members.actions', defaultMessage: 'Actions' })}</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const isAdminRole =
                  member.orgRole === ORG_ROLES.AIBUILD_ADMIN ||
                  member.orgRole === ORG_ROLES.ADMIN;
                const badgeVariant = isAdminRole ? 'admin' : 'member';

                return (
                  <tr key={member.id}>
                    <td>
                      <NameCell>
                        <span>{member.displayName}</span>
                        {member.groups && member.groups.length > 0 && (
                          <InlineChips>
                            {member.groups.map((g) => (
                              <TagPill
                                key={g.id}
                                label={g.name}
                                prefix={false}
                                size="sm"
                                dataTestId={`member-group-chip-${member.id}-${g.id}`}
                              />
                            ))}
                          </InlineChips>
                        )}
                      </NameCell>
                    </td>
                    <td>{member.email}</td>
                    <td>
                      <RoleBadge $variant={badgeVariant}>
                        {ORG_ROLE_LABELS[member.orgRole] || member.orgRole}
                      </RoleBadge>
                    </td>
                    {isAibuildAdmin && (
                      <td>
                        {editingExpiryUserId === member.id ? (
                          <ActionsCell>
                            <DateInput
                              type="date"
                              value={editExpiryValue}
                              min={expiryBounds.min}
                              max={expiryBounds.max}
                              onChange={(e) => setEditExpiryValue(e.target.value)}
                            />
                            <Button variant={BUTTON_VARIANT_PRIMARY} onClick={() => handleSetExpiry(member.id)} stretch={false}>
                              {intl.formatMessage({ id: 'org.save', defaultMessage: 'Save' })}
                            </Button>
                            <Button
                              variant={BUTTON_VARIANT_TEXT}
                              onClick={() => { setEditingExpiryUserId(null); setEditExpiryValue(''); }}
                              stretch={false}
                            >
                              {intl.formatMessage({ id: 'org.cancel', defaultMessage: 'Cancel' })}
                            </Button>
                          </ActionsCell>
                        ) : (
                          <ActionsCell>
                            <span>
                              {member.expiryDate ? new Date(member.expiryDate).toLocaleDateString() : '—'}
                            </span>
                            {member.id !== user?.id && (
                              <SmallIconButton>
                                <IconButton
                                  iconName="edit_0"
                                  variant={ICON_BUTTON_VARIANT_STANDARD}
                                  onClick={() => {
                                    setEditingExpiryUserId(member.id);
                                    setEditExpiryValue(
                                      member.expiryDate
                                        ? new Date(member.expiryDate).toISOString().split('T')[0]
                                        : '',
                                    );
                                  }}
                                  tooltip={{ message: intl.formatMessage({ id: 'org.edit', defaultMessage: 'Edit' }) }}
                                />
                              </SmallIconButton>
                            )}
                          </ActionsCell>
                        )}
                      </td>
                    )}
                    {isAdmin && (
                      <td>
                        {member.id !== user?.id && (
                          <ActionsCell>
                            <RoleDropdownCell>
                              <DropDownField
                                dataTestId={`role-dropdown-${member.id}`}
                                value={ORG_ROLE_LABELS[member.orgRole] || member.orgRole}
                                dropDownMenuItems={buildRoleMenuItems(member.id, member.orgRole)}
                                size={DROP_DOWN_FIELD_SIZE_COMPACT}
                                fullWidthDropDownMenu={false}
                              />
                            </RoleDropdownCell>
                            <CompactRemoveButton>
                              <Button
                                variant={BUTTON_VARIANT_OUTLINE}
                                onClick={() => handleRemoveMember(member.id, member.email)}
                                stretch={false}
                              >
                                {intl.formatMessage({ id: 'org.remove.button', defaultMessage: 'Remove' })}
                              </Button>
                            </CompactRemoveButton>
                          </ActionsCell>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </MemberTable>
        </TableContainer>
      </Frame>

      {isAdmin && !org.isPersonal && (
        <>
          <PageHeader
            variant={PAGE_HEADER_VARIANT_SMALL}
            title={intl.formatMessage({ id: 'org.invite.title', defaultMessage: 'Invite Members' })}
          />
          <Frame>
            <InviteForm onSubmit={handleInvite}>
              <InputGroup>
                <SmallLabel>
                  {intl.formatMessage({ id: 'org.invite.emailLabel', defaultMessage: 'Email address' })}
                </SmallLabel>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                />
              </InputGroup>
              <CompactInputGroup>
                <SmallLabel>
                  {intl.formatMessage({ id: 'org.invite.roleLabel', defaultMessage: 'Role' })}
                </SmallLabel>
                <DropDownField
                  dataTestId="invite-role-dropdown"
                  value={ORG_ROLE_LABELS[inviteRole] || inviteRole}
                  dropDownMenuItems={inviteRoleMenuItems}
                  size={DROP_DOWN_FIELD_SIZE_COMPACT}
                  fullWidthDropDownMenu={false}
                />
              </CompactInputGroup>
              <InviteButtonWrapper>
                <Button variant={BUTTON_VARIANT_PRIMARY} type="submit" disabled={inviteSending} stretch={false}>
                  {inviteSending
                    ? intl.formatMessage({ id: 'org.invite.sending', defaultMessage: 'Sending...' })
                    : intl.formatMessage({ id: 'org.invite.send', defaultMessage: 'Send Invite' })}
                </Button>
              </InviteButtonWrapper>
            </InviteForm>
          </Frame>
        </>
      )}

      {isAdmin && !org.isPersonal && invitations.length > 0 && (
        <>
          <PageHeader
            variant={PAGE_HEADER_VARIANT_SMALL}
            title={intl.formatMessage({ id: 'org.invitations.title', defaultMessage: 'Pending Invitations' })}
          />
          <Frame>
            {invitations.map((inv) => (
              <InviteRow key={inv.id}>
                <InviteInfo>
                  <InviteEmail>{inv.email}</InviteEmail>
                  <InviteMeta>
                    {ORG_ROLE_LABELS[inv.role] || inv.role} &middot;{' '}
                    {intl.formatMessage({ id: 'org.invitations.sent', defaultMessage: 'Sent' })}{' '}
                    {new Date(inv.createdAt).toLocaleDateString()} &middot;{' '}
                    {intl.formatMessage({ id: 'org.invitations.expires', defaultMessage: 'Expires' })}{' '}
                    {new Date(inv.expiresAt).toLocaleDateString()}
                  </InviteMeta>
                </InviteInfo>
                <Button
                  variant={BUTTON_VARIANT_OUTLINE}
                  onClick={() => handleRevokeInvite(inv.id, inv.email)}
                  stretch={false}
                >
                  {intl.formatMessage({ id: 'org.invitations.revoke', defaultMessage: 'Revoke' })}
                </Button>
              </InviteRow>
            ))}
          </Frame>
        </>
      )}

      {!org.isOwnPersonalOrg && (
        <ButtonRow>
          <DangerButtonWrapper>
            <Button
              variant={BUTTON_VARIANT_PRIMARY}
              onClick={handleLeave}
              disabled={members.length <= 1 && invitations.length > 0}
              stretch={false}
            >
              {intl.formatMessage({ id: 'org.leave.button', defaultMessage: 'Leave Organization' })}
            </Button>
          </DangerButtonWrapper>
          {members.length <= 1 && invitations.length > 0 && (
            <HintText>
              {intl.formatMessage({
                id: 'org.leave.pendingInvites',
                defaultMessage: 'Cannot leave while there are pending invitations. Revoke them first.',
              })}
            </HintText>
          )}
        </ButtonRow>
      )}

      {isPersonalOrg && (
        <>
          <PageHeader
            variant={PAGE_HEADER_VARIANT_SMALL}
            title={intl.formatMessage({ id: 'org.switch.title', defaultMessage: 'Switch Organization' })}
          />
          <Frame>
            {showCreateOrg ? (
              <CreateOrgForm onSubmit={handleCreateOrg}>
                <InputGroup>
                  <SmallLabel>
                    {intl.formatMessage({ id: 'org.create.nameLabel', defaultMessage: 'New organization name' })}
                  </SmallLabel>
                  <Input
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'org.create.placeholder', defaultMessage: 'e.g. Acme Corp' })}
                    required
                  />
                </InputGroup>
                <Button variant={BUTTON_VARIANT_PRIMARY} type="submit" disabled={creatingOrg || !newOrgName.trim()} stretch={false}>
                  {creatingOrg
                    ? intl.formatMessage({ id: 'org.create.creating', defaultMessage: 'Creating...' })
                    : intl.formatMessage({ id: 'org.create.button', defaultMessage: 'Create' })}
                </Button>
                <Button
                  variant={BUTTON_VARIANT_TEXT}
                  type="button"
                  onClick={() => { setShowCreateOrg(false); setNewOrgName(''); }}
                  stretch={false}
                >
                  {intl.formatMessage({ id: 'org.cancel', defaultMessage: 'Cancel' })}
                </Button>
              </CreateOrgForm>
            ) : (
              <>
                <SectionDescription>
                  {intl.formatMessage({
                    id: 'org.create.description',
                    defaultMessage: 'Create a new organization to collaborate with a different team. You will be moved to the new organization as its admin.',
                  })}
                </SectionDescription>
                <FrameButtonRow>
                  <Button variant={BUTTON_VARIANT_PRIMARY} onClick={() => setShowCreateOrg(true)} stretch={false}>
                    {intl.formatMessage({ id: 'org.create.team', defaultMessage: 'Create Team Organization' })}
                  </Button>
                </FrameButtonRow>
              </>
            )}
          </Frame>
        </>
      )}
    </Wrapper>
  );
};

export default Organization;
